import React from 'react';
import clsx from 'clsx';

import Button from '../Button';
import { PermissionCheck } from '@/shared';

export type ActionsBarProps = React.PropsWithChildren<{
  selectedCount: number;
  explicitSelectedCount?: number;
  totalCount?: number;
  pageSelected?: boolean;
  selectionMode?: 'explicit' | 'all_filtered';
  className?: string;
  onSelectAll?: () => void;
  onSelectExplicit?: () => void;
  onClearSelection?: () => void;
}>;

const ActionsBarComponent: React.FC<ActionsBarProps> = props => {
  const {
    selectedCount,
    explicitSelectedCount = selectedCount,
    totalCount,
    selectionMode,
    children,
    className,
    onSelectAll,
    onSelectExplicit,
    onClearSelection,
  } = props;

  if (!selectedCount) {
    return null;
  }

  const hasSelectAllControl = Boolean(onSelectAll);
  const allMatchingSelected = selectionMode === 'all_filtered';
  const selectAllCount = totalCount ?? selectedCount;

  return (
    <div
      className={clsx(
        'flex items-center justify-between gap-md rounded-md bg-main-dark dark:bg-main px-lg py-md text-white shadow-lg animate-appear',
        className
      )}
    >
      {hasSelectAllControl ? (
        <div className="flex shrink-0 items-center gap-lg">
          <button
            type="button"
            className={clsx(
              'group flex items-center gap-xs rounded-xs px-xs py-xs text-small transition-default',
              !allMatchingSelected
                ? 'text-white'
                : 'text-light-grey hover:text-white hover:underline'
            )}
            onClick={onSelectExplicit}
          >
            <span className="font-medium">Selected items</span>
            <span
              className={clsx(
                'rounded-full px-sm py-[0.1rem] text-tiny font-semibold transition-default',
                !allMatchingSelected
                  ? 'bg-white text-main-dark'
                  : 'bg-white/10 text-light-grey group-hover:text-white'
              )}
            >
              {explicitSelectedCount}
            </span>
          </button>
          <button
            type="button"
            className={clsx(
              'group flex items-center gap-xs rounded-xs px-xs py-xs text-small transition-default',
              allMatchingSelected
                ? 'text-white'
                : 'text-light-grey hover:text-white hover:underline'
            )}
            onClick={onSelectAll}
          >
            <span className="font-medium">Select All on All Pages</span>
            <span
              className={clsx(
                'rounded-full px-sm py-[0.1rem] text-tiny font-semibold transition-default',
                allMatchingSelected
                  ? 'bg-white text-main-dark'
                  : 'bg-white/10 text-light-grey group-hover:text-white'
              )}
            >
              {selectAllCount}
            </span>
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-x-sm">
          <span className="text-small font-semibold">{selectedCount}</span>
          <span className="font-normal">contacts selected</span>
        </div>
      )}
      <Button
        variant="icon"
        type="ghost"
        icon="clear"
        textSizeClassName="text-tiny"
        action="manage_outreach_contacts"
        className="hover:bg-transparent hover:border-danger hover:text-danger text-light-grey dark:text-white"
        onClick={onClearSelection}
      />
      <div className="flex-1" />
      {children}
    </div>
  );
};

const ActionsBar: typeof ActionsBarComponent = props => {
  return (
    <PermissionCheck action="superadmin">
      <ActionsBar {...props} />
    </PermissionCheck>
  );
};

export default ActionsBarComponent;
